import { createTheme } from "@mui/material/styles";
import { useTheme as useMedosTheme } from "../../react/ThemeProvider";
import { defaultTheme } from "../../core/theme/themes";
import { DEFAULT_FONT_SCALE, scaleCssFontSize, scaleSxFontSize, } from "../../core/theme/fontScale";
import { useMemo } from "react";
const DEFAULT_PRIMARY = "#153B62";
const DEFAULT_PRIMARY_DARK = "#0F2E4D";
const DEFAULT_PRIMARY_LIGHT = "#E7EEF6";
const FONT_FAMILY = "'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const FONT_FAMILY_SERIF = "'Fraunces', Georgia, serif";
function hexToRgba(hex, alpha) {
    const h = hex.replace("#", "");
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b))
        return hex;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
function parsePx(value, fallback) {
    const n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
}
export function createAppointmentTheme(medosTheme) {
    const t = medosTheme ?? defaultTheme;
    const isDefault = t.name === defaultTheme.name &&
        t.colors.primary === defaultTheme.colors.primary;
    const primary = isDefault ? DEFAULT_PRIMARY : t.colors.primary;
    const primaryDark = isDefault ? DEFAULT_PRIMARY_DARK : t.colors.primaryHover;
    const primaryLight = isDefault ? DEFAULT_PRIMARY_LIGHT : hexToRgba(t.colors.primary, 0.15);
    const rMd = t.radii.md !== defaultTheme.radii.md ? parsePx(t.radii.md, 8) : 8;
    const rLg = t.radii.lg !== defaultTheme.radii.lg ? parsePx(t.radii.lg, 12) : 12;
    const fontFamily = t.typography.fontFamily &&
        t.typography.fontFamily !== defaultTheme.typography.fontFamily
        ? t.typography.fontFamily
        : FONT_FAMILY;
    const fontScale = t.typography.fontScale ?? DEFAULT_FONT_SCALE;
    const fs = (value) => scaleCssFontSize(value, fontScale);
    return createTheme({
        palette: {
            primary: {
                main: primary,
                dark: primaryDark,
                light: primaryLight,
                contrastText: "#fff",
            },
            secondary: {
                main: isDefault ? "#153B62" : t.colors.secondary,
                contrastText: "#fff",
            },
            error: {
                main: isDefault ? "#dc2626" : t.colors.error,
                light: isDefault ? "#fef2f2" : t.colors.errorBackground,
            },
            warning: {
                main: isDefault ? "#d97706" : t.colors.warning,
                light: isDefault ? "#fffbeb" : t.colors.warningBackground,
            },
            success: {
                main: isDefault ? "#16a34a" : t.colors.success,
                light: isDefault ? "#dcfce7" : t.colors.successBackground,
            },
            background: {
                default: isDefault ? "#f8fafc" : t.colors.background,
                paper: isDefault ? "#fff" : t.colors.surface,
            },
            text: {
                primary: isDefault ? "#0f172a" : t.colors.text,
                secondary: isDefault ? "#475569" : t.colors.textSecondary,
                tertiary: isDefault ? "#94a3b8" : t.colors.textTertiary,
                disabled: isDefault ? "#cbd5e1" : t.colors.textDisabled,
                onPrimary: isDefault ? "#fff" : t.colors.textOnPrimary,
            },
            divider: isDefault ? "#e5e7eb" : t.colors.border,
            accent: {
                main: primary,
                dark: primaryDark,
                light: primaryLight,
                contrastText: "#fff",
            },
            neutral: {
                bg: isDefault ? "#f3f4f6" : t.colors.background,
                surface: isDefault ? "#fff" : t.colors.surface,
                surfaceHover: isDefault ? "#fafbfc" : t.colors.surfaceHover,
                surfaceSecondary: isDefault ? "#eff6ff" : t.colors.backgroundSecondary,
                border: isDefault ? "#e5e7eb" : t.colors.border,
                borderLight: isDefault ? "#f0f1f3" : t.colors.borderHover,
            },
        },
        typography: {
            fontFamily,
            fontScale,
            h4: { fontFamily, fontWeight: 700, fontSize: fs("2.125rem") },
            h5: { fontFamily, fontWeight: 700, fontSize: fs("1.5rem") },
            h6: { fontFamily, fontWeight: 700, fontSize: fs("1.1rem") },
            subtitle1: { fontWeight: 600, fontSize: fs("0.95rem") },
            subtitle2: { fontWeight: 600, fontSize: fs("0.8rem") },
            body1: { fontSize: fs("0.875rem"), lineHeight: 1.5 },
            body2: { fontSize: fs("0.8125rem"), lineHeight: 1.45 },
            caption: { fontSize: fs("0.75rem"), lineHeight: 1.4 },
            overline: { fontSize: fs("0.75rem") },
            button: { textTransform: "none", fontWeight: 600 },
        },
        shape: { borderRadius: rMd },
        spacing: 8,
        unstable_sxConfig: {
            fontSize: {
                themeKey: "typography",
                transform: (value) => typeof value === "number" || typeof value === "string"
                    ? scaleSxFontSize(value, fontScale)
                    :
                        value,
            },
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: rMd,
                        padding: "10px 24px",
                        fontSize: fs("0.875rem"),
                        boxShadow: "none",
                        "&:hover": { boxShadow: "none" },
                        "&:focus-visible": {
                            outline: `2px solid ${primary}`,
                            outlineOffset: 2,
                        },
                    },
                    containedPrimary: {
                        backgroundColor: primary,
                        "&:hover": { backgroundColor: primaryDark },
                    },
                    outlined: {
                        borderColor: isDefault ? "#e5e7eb" : t.colors.border,
                    },
                },
                defaultProps: { disableElevation: true },
            },
            MuiPaper: {
                styleOverrides: {
                    root: { borderRadius: rLg },
                },
                defaultProps: { elevation: 0 },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: rLg,
                        border: `1px solid ${isDefault ? "#e5e7eb" : t.colors.border}`,
                    },
                },
                defaultProps: { elevation: 0 },
            },
            MuiTextField: {
                defaultProps: { variant: "outlined", size: "small" },
                styleOverrides: {
                    root: {
                        "& .MuiOutlinedInput-root": {
                            borderRadius: rMd,
                        },
                    },
                },
            },
            MuiSelect: {
                defaultProps: { size: "small" },
            },
            MuiChip: {
                styleOverrides: {
                    root: { fontWeight: 500, fontSize: fs("0.75rem") },
                },
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: fs("0.8125rem"),
                        padding: "6px 16px",
                        "&.Mui-selected": {
                            backgroundColor: isDefault ? "#0f172a" : t.colors.text,
                            color: "#fff",
                            "&:hover": {
                                backgroundColor: isDefault ? "#1e293b" : t.colors.text,
                            },
                        },
                    },
                },
            },
            MuiStepIcon: {
                styleOverrides: {
                    root: {
                        color: isDefault ? "#cbd5e1" : t.colors.textDisabled,
                        "&.Mui-active": { color: "#c68a2e" },
                        "&.Mui-completed": { color: "#c68a2e" },
                    },
                    text: { fontWeight: 700, fontSize: fs("0.75rem") },
                },
            },
            MuiStepLabel: {
                styleOverrides: {
                    label: {
                        fontSize: fs("0.8125rem"),
                        fontWeight: 500,
                        "&.Mui-active": { fontWeight: 600 },
                    },
                },
            },
            MuiStepConnector: {
                styleOverrides: {
                    line: {
                        borderColor: isDefault ? "#e5e7eb" : t.colors.border,
                    },
                },
            },
            MuiCssBaseline: {
                styleOverrides: {
                    body: { WebkitFontSmoothing: "antialiased" },
                },
            },
        },
    });
}
export function useAppointmentTheme() {
    const medosTheme = useMedosTheme();
    return useMemo(() => createAppointmentTheme(medosTheme), [medosTheme]);
}
export { FONT_FAMILY, FONT_FAMILY_SERIF };
